// NotificationService - Serviço para gerenciar notificações e alertas da família
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import FamilyService from './familyService';
import { getTodayBRT, convertUTCtoBRT } from '../utils/dateUtils';

// ==================== INTERFACES ====================

export interface Notification {
  id: string;
  memberId: string;
  memberName: string;
  memberAvatar?: string;
  type: 'medication' | 'reminder' | 'health';
  title: string;
  description: string;
  scheduledTime?: string;
  time: string; // Tempo relativo: "Em 30 min", "Atrasado 2h"
  priority: 'high' | 'medium' | 'low';
  category: string; // 'medication' | 'reminder' | 'health'
  medicationId?: string;
  reminderId?: string;
  actions?: string[];
}

// ==================== NOTIFICATION SERVICE ====================
class NotificationService {
  private static instance: NotificationService;
  private familyService: FamilyService;

  // Configurações
  private readonly UPCOMING_THRESHOLD_MINUTES = 60; // Considerar "próxima" se falta menos de 60 min
  private readonly GRACE_PERIOD_MINUTES = 60; // Considerar "dentro do horário" até 60 min após o agendamento

  private constructor() {
    this.familyService = FamilyService.getInstance();
  }
  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // ==================== MÉTODO PRINCIPAL ====================

  /**
   * Busca todas as notificações do dia para a família
   */
  async getFamilyDailyNotifications(familyId: string): Promise<Notification[]> {
    logger.info('🔔 [NotificationService] Buscando notificações da família:', familyId);
    logger.info('🕐 [NotificationService] Horário atual:', new Date().toLocaleString('pt-BR'));

    try {
      const notifications: Notification[] = [];

      // Buscar todos os membros da família
      const members = await this.familyService.getUserFamilyMembers();
      logger.info(`👥 [NotificationService] ${members.length} membros encontrados`);

      if (members.length === 0) {
        logger.warn('⚠️ [NotificationService] Nenhum membro encontrado na família!');
        return [];
      }

      // Buscar notificações de medicação para cada membro
      for (const member of members) {
        if (!member.id) {
          logger.warn(`⚠️ [NotificationService] Membro sem ID: ${member.name}`);
          continue;
        }

        logger.info(`\n👤 [NotificationService] Processando membro: ${member.name} (${member.id})`);

        // Medicações do dia
        const medicationNotifications = await this.getMemberMedicationNotifications(
          member.id,
          member.name,
          member.avatar_url || undefined
        );
        logger.info(`💊 [NotificationService] ${medicationNotifications.length} notificações de medicação para ${member.name}`);
        notifications.push(...medicationNotifications);

        // Lembretes do dia
        const reminderNotifications = await this.getMemberReminderNotifications(
          member.id,
          member.name,
          member.avatar_url || undefined
        );
        logger.info(`🔔 [NotificationService] ${reminderNotifications.length} lembretes para ${member.name}`);
        notifications.push(...reminderNotifications);
      }

      // Ordenar por prioridade e horário
      const sortedNotifications = this.sortNotifications(notifications);

      logger.info(`\n✅ [NotificationService] TOTAL: ${sortedNotifications.length} notificações encontradas`);
      logger.info('📊 [NotificationService] Breakdown:');
      logger.info(`   - Atrasadas: ${sortedNotifications.filter(n => n.title.includes('atrasada')).length}`);
      logger.info(`   - Próximas: ${sortedNotifications.filter(n => n.title.includes('Próxima') || n.title.includes('Hora da')).length}`);
      logger.info(`   - Lembretes: ${sortedNotifications.filter(n => n.type === 'reminder').length}`);
      logger.info(`   - Agendadas: ${sortedNotifications.filter(n => n.title.includes('agendada')).length}`);
      
      return sortedNotifications;
    } catch (error) {
      logger.error('❌ [NotificationService] Erro ao buscar notificações:', error);
      return [];
    }
  }

  // ==================== NOTIFICAÇÕES DE MEDICAÇÃO ====================

  /**
   * Busca notificações de medicação para um membro
   * Usa a mesma lógica do PointsService para consistência
   */
  private async getMemberMedicationNotifications(
    memberId: string,
    memberName: string,
    memberAvatar?: string
  ): Promise<Notification[]> {
    logger.info(`💊 [NotificationService] Buscando medicações de ${memberName}`);

    const notifications: Notification[] = [];
    /* removed unused: const today = new Date(); */
    const todayStr = getTodayBRT();

    try {
      // 1. Buscar medicações ativas do membro
      const { data: medications, error: medError } = await supabase
        .from('medications_v2' as any)
        .select(`
          id,
          member_id,
          remedy_name,
          potency,
          dosage_type,
          quantity,
          times,
          start_date,
          end_date,
          usage_days,
          pause_days,
          total_period_months,
          frequency,
          is_active
        `)
        .eq('member_id', memberId)
        .eq('is_active', true);

      if (medError) throw medError;
      if (!medications || medications.length === 0) {
        logger.info(`   ℹ️ Nenhuma medicação ativa para ${memberName}`);
        return notifications;
      }

      logger.info(`💊 [NotificationService] ${medications.length} medicações ativas encontradas`);

      // 2. Buscar logs de medicação v2 e verificar doses_detail JSONB
      const { data: todayLogs, error: logError } = await supabase
        .from('medication_logs_v2' as any)
        .select('medication_id, time_slot, doses_detail')
        .in('medication_id', medications.map(m => m.id));

      if (logError) throw logError;

      logger.info(`📋 [NotificationService] ${(todayLogs || []).length} logs encontrados`);

      // 3. Criar mapa de logs por medicação e horário para busca rápida
      const logMap = new Map<string, Set<string>>();
      logger.info(`\n📝 [NotificationService] Processando ${(todayLogs || []).length} logs...`);
      
      (todayLogs || []).forEach(log => {
        logger.info(`   Log: medication_id=${log.medication_id}, time_slot=${log.time_slot}`);
        
        // Verificar doses_detail JSONB para ver se há dose tomada hoje
        const dosesDetail = log.doses_detail || {};
        
        // Iterar sobre as doses no JSONB
        Object.entries(dosesDetail).forEach(([_doseNum, doseData]: [string, any]) => {
          // CORRIGIDO (20/10/2025 21:53): Também considerar doses perdidas que foram clicadas
          // Se o usuário clicou fora do prazo (missed com taken_at), não deve mais aparecer no alerta
          const isDoseProcessed = doseData.status === 'taken' || 
                                  doseData.status === 'taken_late' || 
                                  (doseData.status === 'missed' && doseData.taken_at);
          
          if (isDoseProcessed) {
            // Verificar se a dose foi processada hoje
            const takenDate = convertUTCtoBRT(doseData.taken_at || doseData.scheduled_time);
            
            if (takenDate === todayStr) {
              // Usar o time_slot do log (que é o horário agendado)
              const timeStr = log.time_slot.substring(0, 5); // 08:25:00 -> 08:25
              
              logger.info(`   → Dose processada hoje: ${timeStr} (status: ${doseData.status})`);
              
              if (!logMap.has(log.medication_id)) {
                logMap.set(log.medication_id, new Set());
              }
              logMap.get(log.medication_id)!.add(timeStr);
            }
          }
        });
      });
      
      logger.info(`\n🗺️ [NotificationService] Mapa de logs criado:`);
      logMap.forEach((times, medId) => {
        logger.info(`   Medicação ${medId}: [${Array.from(times).join(', ')}]`);
      });

      // 4. Processar cada medicação e seus horários
      for (const medication of medications) {
        const times = medication.times || [];
        logger.info(`\n   📋 Medicação ID: ${medication.id}`);
        logger.info(`   📋 Nome: ${medication.remedy_name} ${medication.potency}`);
        logger.info(`   ⏰ Horários agendados (array): ${JSON.stringify(times)}`);
        logger.info(`   ⏰ Tipo de times: ${typeof times}, é array? ${Array.isArray(times)}`);

        if (times.length === 0) {
          logger.warn(`   ⚠️ Medicação ${medication.remedy_name} não tem horários definidos!`);
          continue;
        }

        const todayStrLocal = getTodayBRT();
const todayLocal = this.parseLocalDate(todayStrLocal)!;
const startDate = this.parseLocalDate(medication?.start_date);
const endDate = this.parseLocalDate(medication?.end_date);

if (startDate && todayLocal < startDate) {
  logger.info('   â­ï¸ Tratamento ainda NÃƒO comeÃ§ou (futuro). Suprimindo notificaÃ§Ãµes.');
  continue;
}

if (endDate && todayLocal >= endDate) {
  logger.info('   â­ï¸ Tratamento jÃ¡ FINALIZOU. Suprimindo notificaÃ§Ãµes.');
  continue;
}

const isPaused = this.isMedicationInPause(medication, todayLocal);
        if (isPaused) {
          logger.info(`   ⏸️ Medicação em pausa atualmente, suprimindo notificações de dose.`);
          const nextUsageDate = this.getNextUsageDate(medication, todayLocal);
          const pauseNotification = this.createPauseNotification(
            medication,
            memberId,
            memberName,
            memberAvatar,
            nextUsageDate
          );
          notifications.push(pauseNotification);
          continue;
        }

        const medicationLogs = logMap.get(medication.id) || new Set();
        logger.info(`   📝 Logs registrados para esta medicação: [${Array.from(medicationLogs).join(', ') || 'Nenhum'}]`);

        // 5. Verificar cada horário agendado
        for (const time of times) {
          logger.info(`\n   🕐 Processando horário: "${time}" (tipo: ${typeof time})`);
          
          // Remover segundos se existirem (21:00:00 -> 21:00)
          const timeWithoutSeconds = time.substring(0, 5);
          logger.info(`      ⏰ Horário normalizado: "${timeWithoutSeconds}"`);
          
          const scheduledDateTime = new Date(`${todayStr}T${timeWithoutSeconds}:00`);
          const now = new Date();

          // Verificar se este horário específico foi registrado
          logger.info(`      🔍 Verificando se "${timeWithoutSeconds}" está em [${Array.from(medicationLogs).join(', ')}]`);
          const wasTaken = medicationLogs.has(timeWithoutSeconds);
          logger.info(`      - Foi tomada? ${wasTaken ? '✅ SIM' : '❌ NÃO'}`);

          if (wasTaken) {
            logger.info(`   ✅ ${medication.remedy_name} às ${time} JÁ FOI TOMADA - ignorando`);
            continue;
          }

          // Calcular diferença em minutos
          const diffMinutes = Math.floor((scheduledDateTime.getTime() - now.getTime()) / 60000);
          logger.info(`      - Agendado: ${scheduledDateTime.toLocaleTimeString('pt-BR')}`);
          logger.info(`      - Atual: ${now.toLocaleTimeString('pt-BR')}`);
          logger.info(`      - Diferença: ${diffMinutes} minutos`);

          let notification: Notification | null = null;

          // DOSE ATRASADA (horário já passou e não foi tomada)
          if (diffMinutes < 0) {
            const minutesPast = Math.abs(diffMinutes);
            if (minutesPast <= this.GRACE_PERIOD_MINUTES) {
              logger.info(`   ⏱️ DOSE AINDA NO HORÁRIO (há ${minutesPast} min)`);
              notification = this.createDueNowNotification(
                medication,
                memberId,
                memberName,
                memberAvatar,
                time,
                minutesPast
              );
            } else {
              logger.info(`   🚨 DOSE ATRASADA! (${minutesPast} min)`);
              notification = this.createOverdueNotification(
                medication,
                memberId,
                memberName,
                memberAvatar,
                time,
                minutesPast
              );
            }
          }
          // DOSE PRÓXIMA (< 60 minutos)
          else if (diffMinutes <= this.UPCOMING_THRESHOLD_MINUTES) {
            logger.info(`   ⏰ PRÓXIMA DOSE! (em ${diffMinutes} min)`);
            notification = this.createUpcomingNotification(
              medication,
              memberId,
              memberName,
              memberAvatar,
              time,
              diffMinutes
            );
          }
          // DOSE FUTURA DO DIA
          else {
            logger.info(`   📅 DOSE FUTURA (em ${diffMinutes} min)`);
            notification = this.createFutureNotification(
              medication,
              memberId,
              memberName,
              memberAvatar,
              time
            );
          }

          if (notification) {
            logger.info(`   ✅ Notificação criada: ${notification.title}`);
            notifications.push(notification);
          }
        }
      }

      return notifications;
    } catch (error) {
      logger.error('❌ [NotificationService] Erro ao buscar medicações:', error);
      return notifications;
    }
  }


  /**
   * Cria notificação de dose ainda dentro do período de tolerância (até 1h depois)
   */
  private createDueNowNotification(
    medication: any,
    memberId: string,
    memberName: string,
    memberAvatar: string | undefined,
    scheduledTime: string,
    minutesPast: number
  ): Notification {
    const timeText = this.formatGracePeriodTime(minutesPast);
    const dosageText = this.formatDosage(medication.dosage_type, medication.quantity);

    return {
      id: `due-${medication.id}-${scheduledTime}`,
      memberId,
      memberName,
      memberAvatar,
      type: 'medication',
      title: 'Hora da Homeopatia!',
      description: `${medication.remedy_name} ${medication.potency} - ${dosageText}`,
      scheduledTime,
      time: timeText,
      priority: 'high',
      category: 'medication',
      medicationId: medication.id,
      actions: ['markAsTaken', 'markAsMissed', 'viewMember']
    };
  }

  /**
   * Cria notificação de dose atrasada
   */
  private createOverdueNotification(
    medication: any,
    memberId: string,
    memberName: string,
    memberAvatar: string | undefined,
    scheduledTime: string,
    minutesOverdue: number
  ): Notification {
    const timeText = this.formatOverdueTime(minutesOverdue);
    const dosageText = this.formatDosage(medication.dosage_type, medication.quantity);

    return {
      id: `overdue-${medication.id}-${scheduledTime}`,
      memberId,
      memberName,
      memberAvatar,
      type: 'medication',
      title: 'Dose atrasada',
      description: `${medication.remedy_name} ${medication.potency} - ${dosageText}`,
      scheduledTime,
      time: timeText,
      priority: 'high',
      category: 'medication',
      medicationId: medication.id,
      actions: ['markAsTaken', 'markAsMissed', 'viewMember']
    };
  }

  /**
   * Cria notificação informando que o tratamento está em pausa
   */
  private createPauseNotification(
    medication: any,
    memberId: string,
    memberName: string,
    memberAvatar: string | undefined,
    resumeDate: Date | null
  ): Notification {
    const dosageText = this.formatDosage(medication.dosage_type, medication.quantity);
    const resumeText = resumeDate ? `Retoma em ${resumeDate.toLocaleDateString('pt-BR')}` : 'Retorno não programado';

    return {
      id: `pause-${medication.id}`,
      memberId,
      memberName,
      memberAvatar,
      type: 'medication',
      title: 'Tratamento em pausa',
      description: `${medication.remedy_name} ${medication.potency} - ${dosageText}`,
      time: resumeText,
      priority: 'low',
      category: 'medication',
      medicationId: medication.id,
      actions: ['viewMember']
    };
  }

  /**
   * Cria notificação de dose próxima
   */
  private createUpcomingNotification(
    medication: any,
    memberId: string,
    memberName: string,
    memberAvatar: string | undefined,
    scheduledTime: string,
    minutesUntil: number
  ): Notification {
    const timeText = this.formatUpcomingTime(minutesUntil);
    const dosageText = this.formatDosage(medication.dosage_type, medication.quantity);

    return {
      id: `upcoming-${medication.id}-${scheduledTime}`,
      memberId,
      memberName,
      memberAvatar,
      type: 'medication',
      title: minutesUntil <= 30 ? 'Hora da Homeopatia!' : 'Próxima dose',
      description: `${medication.remedy_name} ${medication.potency} - ${dosageText}`,
      scheduledTime,
      time: timeText,
      priority: minutesUntil <= 30 ? 'high' : 'medium',
      category: 'medication',
      medicationId: medication.id,
      actions: ['markAsTaken', 'viewMember']
    };
  }

  /**
   * Cria notificação de dose futura do dia
   */
  private createFutureNotification(
    medication: any,
    memberId: string,
    memberName: string,
    memberAvatar: string | undefined,
    scheduledTime: string
  ): Notification {
    const dosageText = this.formatDosage(medication.dosage_type, medication.quantity);

    return {
      id: `future-${medication.id}-${scheduledTime}`,
      memberId,
      memberName,
      memberAvatar,
      type: 'medication',
      title: 'Dose agendada',
      description: `${medication.remedy_name} ${medication.potency} - ${dosageText}`,
      scheduledTime,
      time: `Hoje às ${scheduledTime}`,
      priority: 'low',
      category: 'medication',
      medicationId: medication.id,
      actions: ['viewMember']
    };
  }

  // ==================== NOTIFICAÇÕES DE LEMBRETES ====================

  /**
   * Busca notificações de lembretes para um membro
   */
  private async getMemberReminderNotifications(
    memberId: string,
    memberName: string,
    memberAvatar?: string
  ): Promise<Notification[]> {
    logger.info(`🔔 [NotificationService] Buscando lembretes de ${memberName}`);

    const notifications: Notification[] = [];
    const _today = new Date();
    const todayStr = getTodayBRT();
    const currentDayOfWeek = _today.getDay(); // 0 = domingo, 6 = sábado

    try {
      // Buscar lembretes ativos do membro (seleção mínima)
      const { data: reminders, error } = await supabase
        .from('reminders' as any)
        .select('id, title, description, reminder_date, reminder_time, days_of_week')
        .eq('member_id', memberId)
        .eq('is_active', true);

      if (error) throw error;
      if (!reminders || reminders.length === 0) return notifications;

      logger.info(`🔔 [NotificationService] ${reminders.length} lembretes ativos encontrados`);

      // Processar cada lembrete
      for (const reminder of reminders) {
        let shouldShow = false;
        let reminderDateTime: Date;
        let timeText = '';
        let priority: 'high' | 'medium' | 'low' = 'medium';

        const reminderTime = reminder.reminder_time || '09:00';

        // LEMBRETE ÚNICO (com data específica)
        if (reminder.reminder_date) {
          const reminderDate = new Date(reminder.reminder_date);
          const todayStart = new Date(todayStr);
          const diffDays = Math.floor((reminderDate.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
          
          logger.info(`📅 [Reminder] ${reminder.title}: data=${reminder.reminder_date}, diffDays=${diffDays}`);
          
          // Mostrar se for hoje ou nos próximos 7 dias
          if (diffDays >= 0 && diffDays <= 7) {
            shouldShow = true;
            reminderDateTime = new Date(`${reminder.reminder_date}T${reminderTime}`);
            
            const diffMinutes = Math.floor((reminderDateTime.getTime() - _today.getTime()) / 60000);
            const dateStr = reminderDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
            
            if (diffDays === 0) {
              // É hoje
              if (diffMinutes < 0) {
                timeText = `Hoje - Há ${Math.abs(diffMinutes)} min`;
                priority = 'high';
              } else if (diffMinutes < 60) {
                timeText = `Hoje - Em ${diffMinutes} min`;
                priority = 'high';
              } else {
                timeText = `Hoje às ${reminderTime.substring(0, 5)}`;
                priority = 'medium';
              }
            } else if (diffDays === 1) {
              timeText = `Amanhã às ${reminderTime.substring(0, 5)}`;
              priority = 'medium';
            } else {
              timeText = `${dateStr} às ${reminderTime.substring(0, 5)}`;
              priority = 'low';
            }
          }
        } 
        // LEMBRETE RECORRENTE (dias da semana)
        else if (reminder.days_of_week && reminder.days_of_week.includes(currentDayOfWeek)) {
          shouldShow = true;
          reminderDateTime = new Date(`${todayStr}T${reminderTime}`);
          
          const diffMinutes = Math.floor((reminderDateTime.getTime() - _today.getTime()) / 60000);

          if (diffMinutes < -60) {
            // Passou há mais de 1 hora - não mostrar
            shouldShow = false;
          } else if (diffMinutes < 0) {
            timeText = `Há ${Math.abs(diffMinutes)} min`;
            priority = 'medium';
          } else if (diffMinutes <= 30) {
            timeText = `Em ${diffMinutes} min`;
            priority = 'high';
          } else {
            timeText = `Hoje às ${reminderTime.substring(0, 5)}`;
            priority = 'low';
          }
        }

        if (shouldShow) {
          notifications.push({
            id: `reminder-${reminder.id}`,
            memberId,
            memberName,
            memberAvatar,
            type: 'reminder',
            title: reminder.title || 'Lembrete',
            description: reminder.description || '',
            scheduledTime: reminderTime,
            time: timeText,
            priority,
            category: 'reminder',
            reminderId: reminder.id,
            actions: ['viewMember']
          });
        }
      }

      return notifications;
    } catch (error) {
      logger.error('❌ [NotificationService] Erro ao buscar lembretes:', error);
      return notifications;
    }
  }

  // ==================== HELPERS ====================

  /**
   * Formata tempo de atraso
   */
  private formatOverdueTime(minutes: number): string {
    if (minutes < 60) {
      return `Atrasado ${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `Atrasado ${hours}h`;
    }
    return `Atrasado ${hours}h ${remainingMinutes}min`;
  }

  /**
   * Formata tempo até a dose
   */
  private formatUpcomingTime(minutes: number): string {
    if (minutes === 0) {
      return 'Agora';
    }
    if (minutes < 60) {
      return `Em ${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `Em ${hours}h`;
    }
    return `Em ${hours}h ${remainingMinutes}min`;
  }

  /**
   * Formata tempo passado dentro da tolerância
   */
  private formatGracePeriodTime(minutes: number): string {
    if (minutes === 0) {
      return 'Agora';
    }
    if (minutes < 60) {
      return `Há ${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `Há ${hours}h`;
    }
    return `Há ${hours}h ${remainingMinutes}min`;
  }

  /**
   * Formata a dose para exibição
   */
  private formatDosage(dosageType: string, quantity: number): string {
    if (dosageType === 'globules') {
      return `${quantity} ${quantity === 1 ? 'glóbulo' : 'glóbulos'}`;
    }
    if (dosageType === 'drops') {
      return `${quantity} ${quantity === 1 ? 'gota' : 'gotas'}`;
    }
    return `${quantity} ${dosageType}`;
  }

  private parseLocalDate(dateString?: string | null): Date | null {
    if (!dateString) return null;
    const parts = dateString.split('-').map((part: string) => Number(part));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }

  /**
   * Verifica se a medicação está em pausa para a data informada
   */
  private isMedicationInPause(medication: any, referenceDate: Date): boolean {
    const startDate = this.parseLocalDate(medication?.start_date);
    if (!startDate) return false;
    if (referenceDate < startDate) return false;

    const endDate = this.parseLocalDate(medication?.end_date);
    if (endDate && referenceDate > endDate) return false;

    const totalPeriodMonths = Number(medication?.total_period_months) || 0;
    if (totalPeriodMonths > 0) {
      const plannedEndDate = new Date(startDate);
      plannedEndDate.setDate(plannedEndDate.getDate() + totalPeriodMonths * 30 - 1);
      if (referenceDate > plannedEndDate) return false;
    }

    // Lógica especial para modo custom (dias específicos da semana)
    const frequency = medication?.frequency || '';
    if (frequency.startsWith('custom;')) {
      const parts = frequency.split(';');
      if (parts[1]) {
        try {
          const customDayIndices = parts[1].split(',').map((idx: string) => parseInt(idx));
          const dayOfWeek = referenceDate.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
          
          // Está em pausa se o dia da semana NÃO está na lista
          return !customDayIndices.includes(dayOfWeek);
        } catch {
          // Se falhar o parse, usar lógica padrão
        }
      }
    }

    // Lógica padrão para outros modos (daily, alternate, weekly)
    const usageDays = Number(medication?.usage_days) || 0;
    const pauseDays = Number(medication?.pause_days) || 0;

    if (usageDays <= 0 || pauseDays <= 0) return false;

    const cycleLength = usageDays + pauseDays;
    if (cycleLength <= 0) return false;

    const msPerDay = 24 * 60 * 60 * 1000;
    const daysElapsed = Math.floor((referenceDate.getTime() - startDate.getTime()) / msPerDay);
    const position = ((daysElapsed % cycleLength) + cycleLength) % cycleLength;

    return position >= usageDays;
  }

  /**
   * Calcula a próxima data de uso após o período de pausa
   */
  private getNextUsageDate(medication: any, referenceDate: Date): Date | null {
    const startDate = this.parseLocalDate(medication?.start_date);
    if (!startDate) return null;

    // Lógica especial para modo custom (dias específicos da semana)
    const frequency = medication?.frequency || '';
    if (frequency.startsWith('custom;')) {
      const parts = frequency.split(';');
      if (parts[1]) {
        try {
          const customDayIndices = parts[1].split(',').map((idx: string) => parseInt(idx));
          
          // Buscar o próximo dia válido (1-7 dias à frente)
          for (let i = 1; i <= 7; i++) {
            const nextDate = new Date(referenceDate);
            nextDate.setDate(referenceDate.getDate() + i);
            const nextDayOfWeek = nextDate.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
            
            if (customDayIndices.includes(nextDayOfWeek)) {
              // Verificar se não passou do end_date ou planned_end
              const endDate = this.parseLocalDate(medication?.end_date);
              if (endDate && nextDate > endDate) {
                return null;
              }
              
              const totalPeriodMonths = Number(medication?.total_period_months) || 0;
              if (totalPeriodMonths > 0) {
                const plannedEndDate = new Date(startDate);
                plannedEndDate.setDate(plannedEndDate.getDate() + totalPeriodMonths * 30 - 1);
                if (nextDate > plannedEndDate) {
                  return null;
                }
              }
              
              return nextDate;
            }
          }
          
          return null; // Não deveria chegar aqui se customDays está válido
        } catch {
          // Se falhar o parse, usar lógica padrão
        }
      }
    }

    // Lógica padrão para outros modos (daily, alternate, weekly)
    const usageDays = Number(medication?.usage_days) || 0;
    const pauseDays = Number(medication?.pause_days) || 0;
    const cycleLength = usageDays + pauseDays;

    if (usageDays <= 0 || pauseDays <= 0 || cycleLength <= 0) {
      return null;
    }

    if (referenceDate < startDate) {
      const nextDate = new Date(startDate);
      nextDate.setDate(nextDate.getDate() + usageDays);
      return nextDate;
    }

    const msPerDay = 24 * 60 * 60 * 1000;
    const daysElapsed = Math.floor((referenceDate.getTime() - startDate.getTime()) / msPerDay);
    const cyclesCompleted = Math.floor(daysElapsed / cycleLength);
    const nextCycleStart = new Date(startDate);
    nextCycleStart.setDate(startDate.getDate() + (cyclesCompleted + 1) * cycleLength);

    const endDate = this.parseLocalDate(medication?.end_date);
    if (endDate && nextCycleStart > endDate) {
      return null;
    }

    const totalPeriodMonths = Number(medication?.total_period_months) || 0;
    if (totalPeriodMonths > 0) {
      const plannedEndDate = new Date(startDate);
      plannedEndDate.setDate(plannedEndDate.getDate() + totalPeriodMonths * 30 - 1);
      if (nextCycleStart > plannedEndDate) {
        return null;
      }
    }

    return nextCycleStart;
  }

  /**
   * Ordena notificações por prioridade e horário
   */
  private sortNotifications(notifications: Notification[]): Notification[] {
    const priorityOrder: Record<Notification['priority'], number> = {
      high: 1,
      medium: 2,
      low: 3
    };

    return notifications.sort((a, b) => {
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;

      if (a.scheduledTime && b.scheduledTime) {
        return a.scheduledTime.localeCompare(b.scheduledTime);
      }

      return 0;
    });
  }

  /**
   * Verifica se é um novo dia (para reset à meia-noite)
   */
  isNewDay(): boolean {
    const lastCheck = localStorage.getItem('lastNotificationCheck');
    const currentDate = getTodayBRT();

    if (!lastCheck) {
      localStorage.setItem('lastNotificationCheck', currentDate);
      return true;
    }

    const lastDate = convertUTCtoBRT(lastCheck);

    if (lastDate !== currentDate) {
      localStorage.setItem('lastNotificationCheck', currentDate);
      return true;
    }

    return false;
  }

  /**
   * Marca medicação como tomada
   */
  async markMedicationAsTaken(medicationId: string, scheduledTime: string): Promise<boolean> {
    logger.info('✅ [NotificationService] Marcando medicação como tomada:', medicationId);

    try {
      const today = getTodayBRT();
      // Criar data no horário local (não UTC)
      const localDate = new Date(`${today}T${scheduledTime}:00`);
      const scheduledDateTime = localDate.toISOString(); // Converte para UTC corretamente

      await this.familyService.logMedicationTaken(medicationId, scheduledDateTime, 'taken');
      logger.info('✅ [NotificationService] Medicação marcada como tomada com sucesso');
      return true;
    } catch (error) {
      logger.error('❌ [NotificationService] Erro ao marcar medicação:', error);
      return false;
    }
  }

  /**
   * Marca medicação como perdida
   */
  async markMedicationAsMissed(medicationId: string, scheduledTime: string): Promise<boolean> {
    logger.info('❌ [NotificationService] Marcando medicação como perdida:', medicationId);

    try {
      const today = getTodayBRT();
      // Criar data no horário local (não UTC)
      const localDate = new Date(`${today}T${scheduledTime}:00`);
      const scheduledDateTime = localDate.toISOString(); // Converte para UTC corretamente

      await this.familyService.logMedicationTaken(medicationId, scheduledDateTime, 'missed');
      logger.info('✅ [NotificationService] Medicação marcada como perdida');
      return true;
    } catch (error) {
      logger.error('❌ [NotificationService] Erro ao marcar medicação:', error);
      return false;
    }
  }
}

export default NotificationService;


